import React, { useState, useRef } from 'react';
import { Button } from '@appkit4/react-components/button';
import { Panel } from '@appkit4/react-components';
import MainStructure from '../components/layout/MainStructure/MainStructure';
import { useToast } from '../hooks/useToast';
import UploadSample from '../components/features/UploadForm/UploadForm';
import DiffViewer from '../components/features/DiffViewer/DiffViewer';
import { Difference } from '../types/diffViewer';
import PanelSample from '../components/features/TitlePanel/TitlePanel';
import { panelConfig } from '../config/docCompareConfig';
import { config } from '../config/env.config';
import { Accordion, AccordionItem } from '@appkit4/react-components/accordion';
import DiffReportGenerator from '../components/features/DiffViewer/DiffReport';
import { apiClient } from '../services/apiClient';
import { fetchTextFromSignedUrl, getSignedUrl, uploadFile } from '../utils/fileUpload';

const DIFF_ENDPOINT = config.api.endpoints.diff

interface DoCompareRequest {
  original_file: {
    filename: string;
    ocr: boolean;
    pages: number[] | null;
  };
  modified_file: {
    filename: string;
    ocr: boolean;
    pages: number[] | null;
  };
  html_file: boolean;
}

interface DiffResponse {
  original_signed_url: string;
  modified_signed_url: string;
  differences: Difference[];
  html_diff_url?: string;
}

// getSignedUrl and uploadFile moved to utils/fileUpload.ts

const startDiff = async (params: DoCompareRequest) => {
  return await apiClient.post<DiffResponse>(DIFF_ENDPOINT, params);
};

const DoComparePage: React.FC = () => {
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [modifiedFile, setModifiedFile] = useState<File | null>(null);
  const [originalFileName, setOriginalFileName] = useState('');
  const [modifiedFileName, setModifiedFileName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [diffResult, setDiffResult] = useState<DiffResponse| null>(null);
  const [originalContent, setOriginalContent] = useState<string[]>([]);  // Changed from string to string[]
  const [modifiedContent, setModifiedContent] = useState<string[]>([]); // Changed from string to string[]
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentPage, setCurrentPage] = useState(0);
  const [uploadAccordionKeys, setUploadAccordionKeys] = useState<string[]>(['upload-section']);
  const scrollToLineRef = useRef<((lineNumber: number) => void) | null>(null);
  const { showSuccess, showError, showWarning } = useToast();


  const handleUploadFiles = async () => {
    if (!originalFile || !modifiedFile) {
      showError('Seleziona entrambi i file prima di procedere.');
      return;
    }
    
    setIsUploading(true);
      // First, clear any existing comparison results
    setDiffResult(null);
    setOriginalContent([]);
    setModifiedContent([]);
    setCurrentPage(0); // Reset to first page
  
    try {
      // Get signed URLs for both files concurrently
      const [originalSignedUrl, modifiedSignedUrl] = await Promise.all([
        getSignedUrl(originalFileName, 'application/pdf'),
        getSignedUrl(modifiedFileName, 'application/pdf')
      ]);

      // Upload both files concurrently
      await Promise.all([
        uploadFile(originalSignedUrl, originalFile, 'application/pdf'),
        uploadFile(modifiedSignedUrl, modifiedFile, 'application/pdf')
      ]);
  
      showSuccess('Comparazione avviata con successo.', "",
        { duration: 50000, clickToClose: true }
      );

      const diffParams: DoCompareRequest = {
        original_file: {
          filename: originalFileName,
          ocr: false,  // Hardcoded: always false
          pages: null  // Hardcoded: always null (all pages)
        },
        modified_file: {
          filename: modifiedFileName,
          ocr: false,  // Hardcoded: always false
          pages: null  // Hardcoded: always null (all pages)
        },
        html_file: false  // Hardcoded: always false
      };

      // Let startDiff handle its own errors, including 498
      const conversionResponse = await startDiff(diffParams);
      const differences = Array.isArray(conversionResponse.differences)
        ? conversionResponse.differences
        : [];
      const normalizedResponse: DiffResponse = {
        ...conversionResponse,
        differences,
      };

      setDiffResult(normalizedResponse);
  
      // Fetch content using signed URLs
      const [originalContent, modifiedContent] = await Promise.all([
        fetchContent(normalizedResponse.original_signed_url),
        fetchContent(normalizedResponse.modified_signed_url)
      ]);
      
      setOriginalContent(originalContent);
      setModifiedContent(modifiedContent);

      if (differences.length === 0) {
        showSuccess(
          'Nessuna differenza trovata tra i due documenti.',
          'Comparazione completata',
          { duration: 50000, clickToClose: true }
        );
      }
  
    } catch (error) {
      console.error('Error during file upload');
      
      // Check if it's a password error
      if (error && typeof error === 'object' && 'response' in error && (error as any).response?.status === 400) {
        const errorMessage = (error as any).response?.data?.message;
        
        if (errorMessage && errorMessage.toLowerCase().includes('password')) {
          showWarning(
            'Uno o entrambi i PDF sono protetti da password. La comparazione di PDF protetti non è supportata.',
            'PDF Protetto da Password',
            { duration: 500000, clickToClose: true }
          );
        } else {
          showWarning('Errore durante l\'elaborazione dei file.',"",{ duration: 500000, clickToClose: true });
        }
      } else {
        showError('Si è verificato un errore, riprova.',"",{ duration: 500000, clickToClose: true });
      }
    } finally {
      setIsUploading(false);
    }
  }

  const fetchContent = async (url: string): Promise<string[]> => {
    try {
      const text = await fetchTextFromSignedUrl(url);
      const lines = text.split('\n');
      return lines;
    } catch (error) {
      console.error('Error fetching content');
      throw error;
    }
  };

  const handleOriginalFileChange = (newFile: File | null) => {
    setOriginalFile(newFile);
    if (newFile) {
      const safeName = newFile.name.split(/[\\/]/).pop() || newFile.name;
      setOriginalFileName(safeName);
    }
  };

  const handleModifiedFileChange = (newFile: File | null) => {
    setModifiedFile(newFile);
    if (newFile) {
      const safeName = newFile.name.split(/[\\/]/).pop() || newFile.name;
      setModifiedFileName(safeName);
    }
  };

const handleReset = () => {
  // Reset file states
  setOriginalFile(null);
  setModifiedFile(null);
  setOriginalFileName('');
  setModifiedFileName('');
  
  // Reset diff results
  setDiffResult(null);
  setOriginalContent([]);
  setModifiedContent([]);

  // Reset loading state
  setIsUploading(false);
};

  const [isHovered, setIsHovered] = React.useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <MainStructure>
      <div className="docompare-container">
          <PanelSample config={panelConfig}>
            <div>
              <p>
                <strong>DoCompare</strong> è uno strumento di comparazione che permette di confrontare due versioni di un documento PDF. Lo strumento evidenzia le differenze tra i documenti, facilitando l'identificazione rapida delle modifiche apportate.
              </p>
              <ul>
                <li>Il servizio è in versione <strong>Beta Test.</strong></li>
                <li>I tempi di elaborazione variano in base alla dimensione e complessità dei file.</li>
                <li>
                  Per feedback contattare: <a href="mailto:it_dig_customsolutions@pwc.com" className="email-link">it_dig_customsolutions@pwc.com</a>
                </li>
                <li>
                  <a href="https://pwcita.sharepoint.com/sites/IT-ADV-IT_AST_DIG_PA_COE/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2FIT%2DADV%2DIT%5FAST%5FDIG%5FPA%5FCOE%2FShared%20Documents%2FSviluppi%2F6%2E%20DIG%2FDocument%20Operational%20Toolbox%20%2D%20DOT&viewid=853f9744%2De714%2D4d52%2Db72e%2D8ac987ec1845&p=true&ct=1772100110668&or=OWA%2DNT%2DMail" target="_blank" rel="noopener noreferrer" className="email-link" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    <span className="Appkit4-icon icon-document-text-outline"></span>
                    Analisi funzionale
                  </a>
                </li>
              </ul>
            </div>
          </PanelSample>
          <Accordion 
            multiple={false} 
            onClick={(keys: string[]) => setUploadAccordionKeys(keys)}  // Changed type to string[]
            activeKeys={uploadAccordionKeys}
          >
            <AccordionItem 
              title="Upload Documenti"
              itemKey="upload-section"
            >
              <Panel
                className="ap-panel"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{ 
                  filter: isHovered ? `brightness(90%)` : 'brightness(100%)', 
                  padding: '20px', 
                  borderRadius: '10px', 
                  flex: 1
                }}
              >
                <form onSubmit={(e) => {
                  e.preventDefault();
                  handleUploadFiles();
                }}>
                  <div className={"uploadersContainer"}>
                    <div className={"uploaderWrapper"}>
                      <h3>Documento Original</h3>
                      <UploadSample 
                        onFileChange={handleOriginalFileChange} 
                        acceptFileType='.pdf' 
                        uploadInstruction=''
                      />
                    </div>
                    <div className={"uploaderWrapper"}>
                      <h3>Documento Modificato</h3>
                      <UploadSample 
                        onFileChange={handleModifiedFileChange} 
                        acceptFileType='.pdf' 
                        uploadInstruction='' 
                      />
                    </div>
                  </div>

                  <div className="button-container">
                    <Button
                      kind="primary"
                      onClick={handleUploadFiles}
                      disabled={isUploading || !originalFile || !modifiedFile}
                      loading={isUploading}
                      style={{ width: '250px', marginTop: '20px' }}
                      icon="icon-time-outline"
                    >
                      Compara File
                    </Button>
                    <Button
                      kind="secondary"
                      onClick={handleReset}
                      disabled={isUploading}
                      style={{ width: '250px', marginTop: '20px' }}
                      icon="icon-time-outline"
                    >
                      Reset
                    </Button>
                    {diffResult && originalContent.length > 0 && modifiedContent.length > 0 && (
                    <DiffReportGenerator
                      originalContent={originalContent}
                      modifiedContent={modifiedContent}
                      differences={diffResult.differences}
                      disabled={isUploading}
                    />
                  )}
                  </div>
                </form>
              </Panel>
            </AccordionItem>
          </Accordion>
          

          {diffResult && originalContent.length > 0 && modifiedContent.length > 0 && (
          <>
            <DiffViewer
              originalContent={originalContent}
              modifiedContent={modifiedContent}
              differences={diffResult.differences}
              scrollToLineRef={scrollToLineRef}
            />
          </>

        )}
      </div>
    </MainStructure>
  );
};

export default DoComparePage;
